/**
 * `useCardVisibility(ref, opts)` — gated visibility hook for the
 * Dashboard's lazy-hydrate path.
 *
 * The Dashboard's GameCard mounts three independently-hydrating badges
 * (`InstallStateChip` / `TemplateUpdateBadge` / `InterviewInProgressBadge`)
 * + a per-card status hydrate (`game:status:lite`). On a 110-card grid
 * every one of those used to fire on mount — spinner-faster post-lazy
 * but the renderer kept hammering the network for cards below the fold
 * (per `docs/dashboard-lazy-load-plan.md` lines 109-140).
 *
 * This hook gives every card a single boolean `isVisible` that ALL its
 * child queries gate on via the existing `useIpcQuery(..., { enabled })`
 * + `useQuery({ enabled })` pattern. Cards below the fold stay in the
 * placeholder state; scrolling into view (with a 200 px rootMargin so
 * the hydrate races the user's scroll) flips the flag and the children
 * fire.
 *
 * Implementation: ONE shared `IntersectionObserver` across all cards
 * (keyed by `rootMargin` so cards that opt into a different threshold
 * share an observer with the same options). N-observers-per-grid would
 * be the textbook over-engineering trap — IntersectionObserver is
 * designed to handle many targets, not many instances.
 *
 * The shared observer is held in module scope; the internal registry
 * maps each observed `Element` to the React subscriber callbacks that
 * want to know about its visibility. Cards register on mount, unregister
 * on unmount; when the last subscriber for a given rootMargin disconnects
 * we tear down its observer (so a clean Dashboard unmount doesn't leak).
 */

import { useEffect, useRef, useState, type RefObject } from 'react';

/** Default `rootMargin` matches the plan's 200 px lookahead. */
const DEFAULT_ROOT_MARGIN = '200px';

type Subscriber = (isIntersecting: boolean) => void;

interface SharedObserverEntry {
  observer: IntersectionObserver;
  /** Subscribers keyed by their observed Element. */
  subscribers: Map<Element, Subscriber>;
}

const sharedObservers = new Map<string, SharedObserverEntry>();

function getOrCreateObserver(rootMargin: string): SharedObserverEntry {
  let entry = sharedObservers.get(rootMargin);
  if (entry) return entry;
  // SSR / Node-test fallback — IntersectionObserver isn't available, so
  // we return a stub entry whose observe/unobserve is a no-op and whose
  // subscribers are never invoked. Callers see `isVisible: false`
  // forever in that environment, which is the safe default (no IPC
  // fires, no churn). Mirrors how other renderer hooks fall back when
  // running under `environment: 'node'` in vitest.
  if (typeof IntersectionObserver === 'undefined') {
    const noopObserver = {
      observe: () => {},
      unobserve: () => {},
      disconnect: () => {},
    } as unknown as IntersectionObserver;
    entry = { observer: noopObserver, subscribers: new Map() };
    sharedObservers.set(rootMargin, entry);
    return entry;
  }
  const subscribers = new Map<Element, Subscriber>();
  const observer = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        const cb = subscribers.get(e.target);
        if (cb) cb(e.isIntersecting);
      }
    },
    { rootMargin, threshold: 0 },
  );
  entry = { observer, subscribers };
  sharedObservers.set(rootMargin, entry);
  return entry;
}

function releaseObserver(rootMargin: string): void {
  const entry = sharedObservers.get(rootMargin);
  if (!entry) return;
  if (entry.subscribers.size === 0) {
    entry.observer.disconnect();
    sharedObservers.delete(rootMargin);
  }
}

export interface UseCardVisibilityResult {
  /**
   * True once the observed element has intersected the viewport at
   * least once (offset by `rootMargin`). After flipping true the
   * value stays sticky for the lifetime of the hook — re-hydrate on
   * scroll-back-up is wasteful, the React Query cache already keeps
   * the prior fetch around. Matches plan §"scroll-back-up
   * re-hydrates" mitigation (line 245).
   */
  isVisible: boolean;
}

export interface UseCardVisibilityOptions {
  /** Observer rootMargin (CSS-like string). Default `'200px'`. */
  rootMargin?: string;
}

/**
 * Subscribe `ref.current` to the shared IntersectionObserver for the
 * given `rootMargin` and return whether the element has ever entered
 * the viewport.
 *
 * STICKY semantics: once visible, stays visible. The renderer-level
 * React Query cache holds the hydrated payload across scroll-out /
 * scroll-back-in cycles, so flipping back to `false` would only force
 * a needless re-render without any IPC saving.
 */
export function useCardVisibility(
  ref: RefObject<HTMLElement | null>,
  opts?: UseCardVisibilityOptions,
): UseCardVisibilityResult {
  const rootMargin = opts?.rootMargin ?? DEFAULT_ROOT_MARGIN;
  const [isVisible, setIsVisible] = useState(false);
  // We hold the latest setIsVisible in a ref so the observer callback
  // (long-lived, shared across all cards using this rootMargin) can
  // call the up-to-date subscriber without us having to re-register
  // every render. Without this the subscriber map would leak old
  // callbacks tied to stale closures.
  const stickyRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const entry = getOrCreateObserver(rootMargin);
    const subscriber: Subscriber = (intersecting) => {
      if (intersecting && !stickyRef.current) {
        stickyRef.current = true;
        setIsVisible(true);
        // Once a card has gone visible we don't need to keep observing
        // it — the React Query cache holds the hydrated payload, and a
        // scroll-out doesn't flip us back. Unobserve to keep the
        // shared observer's bookkeeping small even on dashboards with
        // hundreds of historical cards.
        entry.observer.unobserve(el);
        entry.subscribers.delete(el);
      }
    };
    entry.subscribers.set(el, subscriber);
    entry.observer.observe(el);
    return () => {
      // If we unmount before the element ever became visible, clean up
      // our registration so the observer doesn't hold a reference to
      // the detached DOM node.
      if (entry.subscribers.has(el)) {
        entry.observer.unobserve(el);
        entry.subscribers.delete(el);
      }
      releaseObserver(rootMargin);
    };
  }, [ref, rootMargin]);

  return { isVisible };
}

/**
 * Test-only: clear all shared observers between tests so registry
 * state doesn't bleed across test files. Mirrors the
 * `_resetFeatureFlagCacheForTests` pattern.
 */
export function _resetCardVisibilityForTests(): void {
  for (const entry of sharedObservers.values()) {
    entry.observer.disconnect();
  }
  sharedObservers.clear();
}
